import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Gift, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = {
  code: "", trial_days: 30, max_uses: "", expires_at: "", active: true, description: "",
};

export default function AdminPromoCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/promo-codes");
      setCodes(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load promo codes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const submit = async () => {
    const payload = {
      code: form.code.trim().toUpperCase(),
      trial_days: Number(form.trial_days),
      max_uses: form.max_uses ? Number(form.max_uses) : null,
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      active: form.active,
      description: form.description || null,
    };
    if (!payload.code || payload.code.length < 3) {
      toast.error("Code must be at least 3 characters."); return;
    }
    if (!payload.trial_days || payload.trial_days < 1) {
      toast.error("Trial days must be at least 1."); return;
    }
    setSaving(true);
    try {
      await api.post("/admin/promo-codes", payload);
      toast.success(`Promo ${payload.code} created`);
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (code, active) => {
    try {
      await api.patch(`/admin/promo-codes/${code}`, { active });
      setCodes((prev) => prev.map((c) => (c.code === code ? { ...c, active } : c)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete promo code "${code}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/promo-codes/${code}`);
      toast.success(`Promo ${code} deleted`);
      setCodes((prev) => prev.filter((c) => c.code !== code));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="vq-card p-6" data-testid="admin-promo-codes-section">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Gift className="w-4 h-4 text-slate-500" />
            <h3 className="font-display font-bold text-lg text-slate-900">Promo codes</h3>
          </div>
          <p className="text-sm text-slate-600">
            Grant free-trial periods on checkout. Codes are case-insensitive and applied before card capture.
          </p>
        </div>
        <Button
          onClick={openCreate}
          data-testid="admin-promo-new-btn"
          className="bg-slate-900 hover:bg-[#0055FF] text-white h-9"
        >
          <Plus className="w-4 h-4 mr-1" /> New code
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : codes.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded">
          No promo codes yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-2 pr-3">Code</th>
                <th className="text-left py-2 pr-3">Trial</th>
                <th className="text-left py-2 pr-3">Uses</th>
                <th className="text-left py-2 pr-3">Expires</th>
                <th className="text-left py-2 pr-3">Active</th>
                <th className="text-right py-2"></th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <tr key={c.code} className="border-b border-slate-100" data-testid={`admin-promo-row-${c.code}`}>
                  <td className="py-2.5 pr-3">
                    <div className="font-mono font-semibold text-slate-900">{c.code}</div>
                    {c.description && <div className="text-xs text-slate-500">{c.description}</div>}
                  </td>
                  <td className="py-2.5 pr-3 text-slate-700">{c.trial_days}d</td>
                  <td className="py-2.5 pr-3 text-slate-700 font-mono text-xs">
                    {c.uses}{c.max_uses != null ? ` / ${c.max_uses}` : " / ∞"}
                  </td>
                  <td className="py-2.5 pr-3 text-slate-500 text-xs">
                    {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="py-2.5 pr-3">
                    <Switch
                      checked={!!c.active}
                      onCheckedChange={(v) => toggleActive(c.code, v)}
                      data-testid={`admin-promo-toggle-${c.code}`}
                    />
                  </td>
                  <td className="py-2.5 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(c.code)}
                      data-testid={`admin-promo-delete-${c.code}`}
                      className="text-slate-500 hover:text-red-600 h-8"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="admin-promo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">New promo code</DialogTitle>
            <DialogDescription className="sr-only">
              Create a new promo code that grants a free trial to customers at checkout.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Code</Label>
              <Input
                data-testid="admin-promo-form-code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="LAUNCH15"
                maxLength={40}
                className="font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Trial days</Label>
                <Input
                  data-testid="admin-promo-form-trial-days"
                  type="number" min="1" max="365"
                  value={form.trial_days}
                  onChange={(e) => setForm({ ...form, trial_days: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Max uses (optional)</Label>
                <Input
                  data-testid="admin-promo-form-max-uses"
                  type="number" min="1"
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  placeholder="∞ unlimited"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Expires (optional)</Label>
              <Input
                data-testid="admin-promo-form-expires"
                type="date"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Description (optional)</Label>
              <Textarea
                data-testid="admin-promo-form-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                maxLength={200}
                className="resize-none"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                data-testid="admin-promo-form-active"
              />
              <span>Active immediately</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              data-testid="admin-promo-form-submit"
              className="bg-slate-900 hover:bg-[#0055FF] text-white"
            >
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Create promo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
